import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Mail, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/shared/auth/useAuth";
import { Button } from "@/shared/components/ui/button";

const COOLDOWN_SECONDS = 60;
const BANNER_KEY = "email_verification";

export function VerificationBanner() {
  const { t } = useTranslation("common");
  const { user, isEmailVerified, resendVerificationEmail, preferences, updatePreferences } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  const dismissed = preferences?.dismissedBanners?.includes(BANNER_KEY) ?? false;

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = useCallback(async () => {
    setSending(true);
    setError(null);
    setSent(false);
    const result = await resendVerificationEmail();
    setSending(false);
    if (result.error) {
      setError(result.error);
    } else {
      setSent(true);
      setCooldown(COOLDOWN_SECONDS);
    }
  }, [resendVerificationEmail]);

  const handleDismiss = useCallback(() => {
    const current = preferences?.dismissedBanners ?? [];
    if (!current.includes(BANNER_KEY)) {
      updatePreferences({ dismissedBanners: [...current, BANNER_KEY] });
    }
  }, [preferences?.dismissedBanners, updatePreferences]);

  // Don't show if verified, dismissed, or no user
  if (isEmailVerified || dismissed || !user) return null;

  return (
    <div className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
      <Mail className="size-4 shrink-0" />
      <p className="flex-1">
        {t("verification.banner", { defaultValue: "Verify your email to unlock all features." })}
        {user.email && (
          <span className="ml-1 font-medium">{user.email}</span>
        )}
      </p>
      {sent && !error && (
        <span className="flex items-center gap-1.5 text-xs text-emerald-700">
          <CheckCircle2 className="size-3.5" />
          {t("verification.sent", { defaultValue: "Email sent!" })}
        </span>
      )}
      {error && (
        <span className="flex items-center gap-1.5 text-xs text-red-700">
          <AlertTriangle className="size-3.5" />
          {error}
        </span>
      )}
      <Button
        variant="outline"
        size="sm"
        className="h-7 shrink-0 border-amber-300 bg-white/80 text-xs text-amber-800 hover:bg-amber-100"
        disabled={sending || cooldown > 0}
        onClick={handleResend}
      >
        {sending ? (
          <Loader2 className="mr-1.5 size-3 animate-spin" />
        ) : null}
        {cooldown > 0
          ? `${t("verification.resendIn", { defaultValue: "Resend in" })} ${cooldown}s`
          : t("verification.resend", { defaultValue: "Resend email" })}
      </Button>
      <button
        type="button"
        className="shrink-0 rounded p-0.5 text-amber-600 hover:bg-amber-200 hover:text-amber-900"
        onClick={handleDismiss}
        aria-label={t("actions.close")}
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
